import { useCallback, useEffect, useMemo, useState } from 'react';

import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { DataTable } from '@/components/data-table/data-table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useModal } from '@/hooks/use-modal';
import { IntegrationConnectionTrigger } from '@/modules/common/components/integration-connection-trigger';
import { useFeatureFlags } from '@/modules/common/hooks';
import { useOAuthProcessor } from '@/modules/common/hooks/use-oauth-processor';
import { IntegrationServices, IntegrationState } from '@/modules/common/types/integrations';
import { PollingInterval } from '@/modules/common/types/polling-interval';
import { RequestModal } from '@/modules/onboarding/integrations-setup/components/modals/request-modal';
import { mergeIntegrationsWithStatus } from '@/modules/onboarding/integrations-setup/helpers/merge-integrations';
import { useIntegrationStatus } from '@/modules/onboarding/integrations-setup/hooks/use-integration-status';
import { logger } from '@/utils/logger/logger';

import { AllIntegrationsRefreshCard } from '../../components/integrations/all-integrations-refresh-card';
import { getIntegrationsTableColumns } from '../../components/integrations/integrations-table-columns';
import { useSynchronizeIntegration } from '../../hooks';

const TAG = 'IntegrationsPage:';

export const IntegrationsPage = () => {
  const { t } = useTranslation(['authenticated.settings', 'authenticated.onboarding.integrations']);
  const { isDemoEnv } = useFeatureFlags();

  // Process OAuth callback if present in URL
  useOAuthProcessor();

  // Track which integration's connection modal is open
  const [activeConnectionService, setActiveConnectionService] =
    useState<IntegrationServices | null>(null);

  const [pollingInterval, setPollingInterval] = useState(PollingInterval.LONG);

  const synchronizeMutation = useSynchronizeIntegration();

  const { data: integrationStatusData } = useIntegrationStatus({
    enablePolling: activeConnectionService === null, // Pause polling when modal is open
    pollingInterval: pollingInterval,
  });

  const requestModalProps = useModal();

  const handleRefresh = useCallback(
    (service: IntegrationServices) => {
      synchronizeMutation.mutate({ integrations: [service] });
    },
    [synchronizeMutation]
  );

  const handleConnect = useCallback((service: IntegrationServices) => {
    setActiveConnectionService(service);
  }, []);

  const handleCloseConnectionModal = useCallback(() => {
    setActiveConnectionService(null);
  }, []);

  // Merge API integration status with local integration definitions
  const integrations = useMemo(() => {
    if (!integrationStatusData) {
      return mergeIntegrationsWithStatus([]);
    }
    return mergeIntegrationsWithStatus(integrationStatusData.integrations);
  }, [integrationStatusData]);

  // Update syncing status when integration status changes
  useEffect(() => {
    if (integrations.some(integration => integration.state == IntegrationState.SYNCING)) {
      logger.trace(TAG, 'Integration is syncing, setting polling interval to SHORT');
      setPollingInterval(PollingInterval.SHORT);
    } else {
      logger.trace(TAG, 'No integrations are syncing, setting polling interval to LONG');
      setPollingInterval(PollingInterval.LONG);
    }
  }, [integrations, setPollingInterval]);

  const columns = useMemo(
    () =>
      getIntegrationsTableColumns({
        onRefresh: handleRefresh,
        onConnect: handleConnect,
        refreshingService:
          synchronizeMutation.isPending && synchronizeMutation.variables?.integrations
            ? (synchronizeMutation.variables.integrations[0] ?? null)
            : null,
      }),
    [handleRefresh, handleConnect, synchronizeMutation.isPending, synchronizeMutation.variables]
  );

  const table = useReactTable({
    data: integrations,
    columns,
    getCoreRowModel: getCoreRowModel(),
    // Maintain stable row identities to prevent modal from closing during polling
    getRowId: row => row.service,
  });

  return (
    <>
      <div className="absolute inset-0 flex flex-col overflow-hidden py-6">
        <Card
          className="flex min-h-0 w-full flex-1 flex-col overflow-hidden p-6"
          style={{ maxWidth: '1000px' }}
        >
          {/* Card Header - Fixed */}
          <div className="mb-6 shrink-0">
            <h2 className="text-lg font-semibold leading-7">
              {t('authenticated.settings:pages.integrations.title')}
            </h2>

            {integrationStatusData && (
              <div className="mt-4">
                <AllIntegrationsRefreshCard integrationStatusData={integrationStatusData} />
              </div>
            )}

            {isDemoEnv && (
              <Alert variant="warning" className="mt-4">
                <AlertTriangle className="h-5 w-5" />
                <AlertDescription>
                  {t('authenticated.settings:pages.integrations.demoWarning')}
                </AlertDescription>
              </Alert>
            )}
          </div>

          {/* Table with Scrollable Body */}
          <div className="flex min-h-0 flex-1 flex-col">
            <DataTable table={table} stickyHeader className="min-h-0" />

            <div className="mt-2 flex shrink-0 items-center justify-end gap-4 pr-4">
              <p className="text-sm">
                {t('authenticated.settings:pages.integrations.findToolText')}
              </p>

              <Button size="sm" onClick={requestModalProps.onOpen}>
                {t('authenticated.settings:pages.integrations.requestIntegrationButton')}
              </Button>
            </div>
          </div>
        </Card>
      </div>

      <RequestModal {...requestModalProps} />

      {activeConnectionService && (
        <IntegrationConnectionTrigger
          service={activeConnectionService}
          open={true}
          onOpenChange={open => {
            if (!open) {
              handleCloseConnectionModal();
            }
          }}
        />
      )}
    </>
  );
};
